import React, { useState } from "react";
import { TextField, Button, Box, Typography, Alert } from "@mui/material";
import { useLocale } from "next-intl";
import { couponBlockedName, orderBundleFields } from "src/utils/bundles";
import { useAppTranslate } from "src/hooks/useAppTranslate";
import { CouponType } from "src/enums/coupon-type";
import { useOrder } from "@contexts/index";
import { useOrderQuery } from "src/hooks";
import { GetCouponDto } from "src/services/generated-api";
import { trackStep, trackLoss } from "src/utils/funnel";
import {
  CouponRejectedError,
  couponErrorMessage,
} from "src/utils/validate-coupon";
import { formatCurrency } from "@configs/currencies";
import type { CouponApplyOutcome } from "src/utils/apply-coupon";
import { useOrderCashback } from "src/hooks/useCashback";
import { cashbackAmountKey, couponCashbackLine } from "src/utils/cashback";
import { couponValueLabel, isCashbackCoupon } from "src/utils/coupon-reward";

interface CouponSectionProps {
  coupon: GetCouponDto | null | undefined;
  couponValid: boolean;
  setCouponValid: (valid: boolean) => void;
  validateCoupon: (code: string) => Promise<GetCouponDto>;
  /**
   * Throws only when add-coupon refuses the code. Resolves "stale" when the
   * coupon was added but the order could not be re-read (review I3).
   */
  applyCoupon: (
    coupon: GetCouponDto,
  ) => void | Promise<void | CouponApplyOutcome>;
  removeCoupon?: () => Promise<void>;
  /** The payment choice on screen (delivery step), so this asks under the checkout note's key. */
  paymentOption?: string;
  /**
   * The order's cashback note renders below (delivery step) and already says
   * what the customer will receive: the chip then does not repeat it.
   */
  rewardShownBelow?: boolean;
  /**
   * The cart drawer: credit is not on the cart yet and may lower the reward
   * at the delivery step (`creditMayLowerCashback`), so it reads as "up to".
   */
  creditMayApply?: boolean;
  /**
   * The canvas page: it sits among that page's own headings, so the
   * "Do you have a coupon?" title and the outer margins are left to the host.
   */
  embedded?: boolean;
}

const CouponSection: React.FC<CouponSectionProps> = ({
  coupon,
  couponValid,
  setCouponValid,
  validateCoupon,
  applyCoupon,
  removeCoupon,
  paymentOption,
  rewardShownBelow,
  creditMayApply,
  embedded,
}) => {
  const [couponCode, setCouponCode] = useState("");
  const { mainOrderId, guestToken } = useOrder();
  const { data: order } = useOrderQuery(mainOrderId, guestToken);
  const locale = useLocale();
  // A bundle with coupons off is applying: the coupon stays attached (it works
  // again if the bundle stops applying) but gives nothing now, and says why.
  const blockedBy = couponBlockedName(
    {
      couponUsed: (order as { couponUsed?: unknown } | undefined)?.couponUsed,
      appliedBundles: orderBundleFields(order).appliedBundles,
    },
    locale,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  const translate = useAppTranslate();

  // A cashback coupon discounts nothing: its chip names the credit, and the
  // line under it is the server's cashback view, never a local sum.
  const cashbackCoupon = isCashbackCoupon(coupon);
  const currency = (order as { currency?: string } | undefined)?.currency ?? "eur";
  const fmt = (cents: number) => formatCurrency(cents / 100, currency);
  // Same key as the checkout note (amount, credit, coupon, payment choice):
  // one request, and adding or removing a 0-priced coupon asks again.
  const { data: cashbackView } = useOrderCashback(
    mainOrderId,
    cashbackAmountKey(order as Parameters<typeof cashbackAmountKey>[0]),
    {
      guestToken,
      paymentOption,
      couponIsCashback: true,
      enabled: cashbackCoupon && couponValid && !blockedBy,
    },
  );
  const cashbackLine =
    cashbackCoupon && !blockedBy
      ? couponCashbackLine(cashbackView, fmt, { rewardShownBelow, creditMayApply })
      : null;

  const handleApplyClick = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!couponCode.trim() || loading || (couponValid && !!coupon)) {
      return;
    }

    setLoading(true);
    setError(null);

    const attempted = couponCode.trim();
    trackStep("coupon-submitted", { code: attempted });

    // Which request failed: check-coupon is currency-blind, so a code from
    // another shop passes it and only add-coupon refuses it (404).
    let stage: "lookup-failed" | "apply-failed" = "lookup-failed";
    try {
      const validatedCoupon = await validateCoupon(attempted);
      stage = "apply-failed";
      // Awaited: un-awaited, add-coupon's 404 escaped this catch, the field
      // cleared and the customer was told nothing (currency walk B2).
      const outcome = await applyCoupon(validatedCoupon);
      setCouponValid(true);
      setCouponCode("");
      // Applied on the server, but the new total could not be loaded: never
      // "invalid", just ask for a retry so the price on screen is right.
      if (outcome === "stale") setError(translate("checkout.tryAgain"));
    } catch (err: unknown) {
      // The local rules carry English sentences (they are also the funnel's
      // `message`); the customer reads the translated ones.
      const message =
        err instanceof CouponRejectedError && err.reason === "expired"
          ? translate("order.couponExpired")
          : err instanceof CouponRejectedError &&
              err.reason === "minimum-amount" &&
              err.minimumCents !== undefined
            ? translate("order.couponMinimum", {
                amount: fmt(err.minimumCents),
              })
            : (couponErrorMessage(err) ?? translate("order.couponInvalid"));
      setError(message);
      setCouponValid(false);
      // A refused code used to leave no trace anywhere: the customer saw an
      // inline error and we saw nothing at all. So "are customers still trying
      // a campaign code we expired?" was unanswerable — which is exactly the
      // question the 1 August conversion drop turned on. The code itself is a
      // marketing string, not personal data, and it is the whole point of the
      // report: it names WHICH code is being refused.
      // `reason` is the stable enum, not the sentence. The sentence varies with
      // the cart ("Minimum order amount of 95.9 EUR required") so as a tag it
      // would both explode cardinality and — at 41 characters — quietly fall
      // past trackLoss's 40-char promotion cutoff and stop being filterable at
      // all. The prose still rides along in `extra` as `message`.
      trackLoss("coupon-rejected", {
        code: attempted,
        reason: err instanceof CouponRejectedError ? err.reason : stage,
        message,
        status: (err as { response?: { status?: number } })?.response?.status,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!removeCoupon || removing) {
      return;
    }

    setRemoving(true);
    setError(null);

    try {
      await removeCoupon();
      setCouponCode("");
    } catch (err: unknown) {
      setError(couponErrorMessage(err) ?? translate("checkout.tryAgain"));
    } finally {
      setRemoving(false);
    }
  };

  const removeAction = removeCoupon && (
    <Button
      color="inherit"
      size="small"
      onClick={handleRemoveClick}
      disabled={removing}
    >
      {removing ? translate("common.loading") : translate("common.remove")}
    </Button>
  );

  return (
    <Box sx={embedded ? undefined : { mt: 3, mb: 2 }}>
      {!embedded && (
        <Typography variant="h5" sx={{ my: 1, px: "2px" }}>
          {translate("order.coupon")}
        </Typography>
      )}
      {couponValid && coupon && !blockedBy && (
        <Alert
          severity="success"
          sx={{ mb: 2, border: "1px solid green" }}
          action={removeAction}
        >
          {translate("order.couponApplied")}: <b>{coupon.code}</b> (
          {cashbackCoupon
            ? translate("cashback.couponValue", {
                value: couponValueLabel(coupon, fmt, locale),
              })
            : coupon.type === CouponType.PERCENTAGE
              ? `${coupon.discount}%`
              : `${coupon.discount / 100} ${order?.currency}`}
          )
          {cashbackLine ? (
            <Typography
              variant="body2"
              data-testid="coupon-cashback-line"
              sx={{ mt: 0.5 }}
            >
              {translate(cashbackLine.key, cashbackLine.params)}
            </Typography>
          ) : null}
        </Alert>
      )}
      {blockedBy ? (
        // Attached but giving nothing: never the green "applied" state.
        <Alert
          severity="warning"
          data-testid="coupon-blocked-by-bundle"
          sx={{ mb: 2 }}
          action={couponValid && coupon ? removeAction : undefined}
        >
          {coupon?.code ? (
            <>
              <b>{coupon.code}</b>:{" "}
            </>
          ) : null}
          {translate("bundles.couponBlocked", { name: blockedBy })}
        </Alert>
      ) : null}
      {error && (
        <Alert severity="error" sx={{ mb: 2, border: "1px solid red" }}>
          {error}
        </Alert>
      )}{" "}
      <Box component="div" sx={{ display: "flex" }}>
        <TextField
          value={couponCode}
          onChange={(e) => setCouponCode(e.target.value)}
          placeholder={translate("order.couponPlaceholder")}
          variant="outlined"
          size="small"
          fullWidth
          disabled={loading || (couponValid && !!coupon)}
          sx={{ mr: 1 }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleApplyClick(e);
            }
          }}
        />
        <Button
          onClick={handleApplyClick}
          variant="contained"
          disabled={!couponCode.trim() || loading || (couponValid && !!coupon)}
        >
          {loading
            ? translate("common.loading")
            : couponValid && coupon && !blockedBy
            ? translate("common.applied")
            : translate("common.apply")}
        </Button>
      </Box>
    </Box>
  );
};

export default CouponSection;
